import { Message } from '@monetize/types/app';
import React, { useMemo } from 'react';
import { MFlex } from '../Monetize';
import { ActivityGroup } from './ActivityGroup';
import { buildChatTurns } from './chatTurns';
import { MessageItem } from './MessageItem';
import { CHAT_TURN_ATTRIBUTE, PINNED_TURN_OFFSET_PX } from './useChatScroll';

/** Matches the conversation log's vertical padding in ChatContainer. */
const LOG_PADDING_Y_PX = 20;

/**
 * The latest turn fills the panel (the scroll area is a size container), so
 * a just-sent prompt can be scrolled to the top with room for its reply to
 * grow beneath it.
 */
const LATEST_TURN_MIN_HEIGHT = `calc(100cqh - ${
  PINNED_TURN_OFFSET_PX + LOG_PADDING_Y_PX
}px)`;

export interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  isReconnecting?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  isReconnecting,
}) => {
  const turns = useMemo(
    () => buildChatTurns(messages, isLoading),
    [messages, isLoading],
  );

  return (
    <>
      {turns.map(({ key, hasPrompt, items }, turnIndex) => (
        <MFlex
          key={key}
          direction="column"
          gap={4}
          minH={
            hasPrompt && turnIndex === turns.length - 1
              ? LATEST_TURN_MIN_HEIGHT
              : undefined
          }
          {...(hasPrompt && { [CHAT_TURN_ATTRIBUTE]: '' })}
        >
          {items.map((item) => {
            if (item.kind === 'message') {
              return (
                <MessageItem
                  key={item.key}
                  message={item.message}
                  showAvatar={item.showAvatar}
                  isLatestReply={item.isLatestReply}
                />
              );
            }
            if (item.kind === 'activity') {
              return (
                <ActivityGroup
                  key={item.key}
                  activity={item.activity}
                  segmentIndex={item.segmentIndex}
                  showAvatar={item.showAvatar}
                  isLive={item.isLive}
                  isReconnecting={item.isLive && isReconnecting}
                />
              );
            }
            return (
              <ActivityGroup
                key={item.key}
                showAvatar={item.showAvatar}
                isLive
                isReconnecting={isReconnecting}
              />
            );
          })}
        </MFlex>
      ))}
    </>
  );
};
